import { ArrowLeft, Moon, TrendingUp } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const SleepDetail = () => {
  const navigate = useNavigate();

  const weeklyData = [
    { day: "Mon", hours: 7.5, deep: 1.5, light: 4.8, rem: 1.2 },
    { day: "Tue", hours: 8.2, deep: 1.8, light: 5.2, rem: 1.2 },
    { day: "Wed", hours: 6.8, deep: 1.2, light: 4.3, rem: 1.3 },
    { day: "Thu", hours: 8.5, deep: 2.0, light: 5.1, rem: 1.4 },
    { day: "Fri", hours: 7.2, deep: 1.4, light: 4.6, rem: 1.2 },
    { day: "Sat", hours: 9.0, deep: 2.2, light: 5.4, rem: 1.4 },
    { day: "Sun", hours: 8.5, deep: 1.9, light: 5.2, rem: 1.4 },
  ];

  const monthlyData = [
    { week: "Week 1", hours: 52.5 },
    { week: "Week 2", hours: 55.8 },
    { week: "Week 3", hours: 51.2 },
    { week: "Week 4", hours: 57.2 },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/50 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">Sleep</h1>
            <p className="text-xs text-muted-foreground">
              Your rest and recovery
            </p>
          </div>
        </div>

        <div className="space-y-4 p-4">
          {/* Last Night's Summary */}
          <Card className="from-info/5 to-info/10 rounded-2xl border-0 bg-gradient-to-br p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-4">
              <div className="bg-info/20 flex h-14 w-14 items-center justify-center rounded-xl">
                <Moon className="text-info h-7 w-7" />
              </div>
              <div className="flex-1">
                <div className="text-3xl font-bold text-foreground">8.5</div>
                <p className="text-sm text-muted-foreground">
                  hours last night
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 border-t border-border/30 pt-3">
              <div className="text-center">
                <div className="text-chart-1 text-lg font-bold">1.9h</div>
                <div className="text-xs text-muted-foreground">Deep</div>
              </div>
              <div className="text-center">
                <div className="text-chart-2 text-lg font-bold">5.2h</div>
                <div className="text-xs text-muted-foreground">Light</div>
              </div>
              <div className="text-center">
                <div className="text-chart-3 text-lg font-bold">1.4h</div>
                <div className="text-xs text-muted-foreground">REM</div>
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="rounded-2xl border-0 bg-muted/30 p-4 shadow-sm">
            <div className="flex gap-3">
              <TrendingUp className="text-info mt-0.5 h-5 w-5 flex-shrink-0" />
              <div>
                <h4 className="mb-1 text-sm font-semibold text-foreground">
                  About Sleep Tracking
                </h4>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Sleep stages are tracked using heart rate variability and
                  movement patterns. Deep sleep is crucial for physical
                  recovery, while REM sleep supports memory and learning. Adults
                  need 7-9 hours nightly.
                </p>
              </div>
            </div>
          </Card>

          {/* Tabs for Time Periods */}
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-2">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="space-y-4">
              <Card className="rounded-2xl border-0 p-5 shadow-sm">
                <h3 className="mb-4 font-semibold text-foreground">
                  This Week's Sleep
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                        opacity={0.3}
                      />
                      <XAxis
                        dataKey="day"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        domain={[0, 10]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Bar
                        dataKey="hours"
                        fill="hsl(var(--info))"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="monthly" className="space-y-4">
              <Card className="rounded-2xl border-0 p-5 shadow-sm">
                <h3 className="mb-4 font-semibold text-foreground">
                  This Month's Sleep
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                        opacity={0.3}
                      />
                      <XAxis
                        dataKey="week"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Bar
                        dataKey="hours"
                        fill="hsl(var(--info))"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default SleepDetail;
